<template>
  <div class="prose prose-lg max-w-none">
    <p class="text-gray-600 mb-6">Last updated: 09 March 2026</p>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">1. The Bindle Platform</h2>
      <p class="text-gray-700 mb-4">
        Bindle is an online learning platform that enables students to access educational services provided by independent tutors. Tutors may offer recorded educational materials, group tutoring sessions, and one-to-one tutoring sessions.
      </p>
      <p class="text-gray-700 mb-4">
        Tutors operate as independent service providers. Bindle provides the platform infrastructure, subscription management and payment processing but does not directly provide tutoring services.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">2. Student Accounts</h2>
      <p class="text-gray-700 mb-4">
        To access Bindle services, students must create an account and provide accurate and complete information. You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account.
      </p>
      <p class="text-gray-700 mb-4">
        Bindle reserves the right to suspend or terminate accounts that violate these Terms or misuse the platform.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">3. Student Age Requirements</h2>
      <p class="text-gray-700 mb-4">
        Bindle may be used by students under the age of 18. However, if you are under 18, a parent or legal guardian must review and accept these Terms on your behalf.
      </p>
      <p class="text-gray-700 mb-4">
        By creating an account or using the platform, you confirm that either:
      </p>
      <ul class="list-disc pl-6 text-gray-700 mb-4 space-y-2">
        <li>you are at least 18 years old, or</li>
        <li>a parent or legal guardian has accepted these Terms and agrees to be responsible for your use of the platform.</li>
      </ul>
      <p class="text-gray-700 mb-4">
        Bindle does not knowingly enter into contracts directly with individuals under the age of 18 without parental or guardian consent.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">4. Tutor Subscriptions</h2>
      <p class="text-gray-700 mb-4">
        Tutors offer subscription-based learning services through the Bindle platform. Subscriptions may include access to recorded educational content, scheduled group tutoring sessions, and scheduled one-to-one tutoring sessions.
      </p>
      <p class="text-gray-700 mb-4">
        Subscription pricing and features are determined by each tutor and are displayed on the tutor's page prior to purchase. By subscribing to a tutor's offering, you agree to the subscription price and billing frequency associated with that plan.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">5. Payments</h2>
      <p class="text-gray-700 mb-4">
        Payments for subscriptions are processed securely through third-party payment providers. By purchasing a subscription, you authorise Bindle to charge your selected payment method on a recurring basis until the subscription is cancelled.
      </p>
      <p class="text-gray-700 mb-4">
        Subscription fees are charged in advance of the relevant billing period.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">6. Cancellation of Subscriptions</h2>
      <p class="text-gray-700 mb-4">
        Students may cancel a subscription at any time through their Bindle account. If a subscription is cancelled:
      </p>
      <ul class="list-disc pl-6 text-gray-700 mb-4 space-y-2">
        <li>cancellation will apply to future billing periods only,</li>
        <li>the subscription remains active until the end of the current billing cycle, and</li>
        <li>no refunds will be issued for unused time during the current billing period.</li>
      </ul>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">7. Scheduling and Attendance</h2>
      <p class="text-gray-700 mb-4">
        Tutors determine their availability and publish scheduled sessions through the platform. For one-to-one tutoring, students typically book a recurring weekly session with a tutor. For group tutoring, tutors run scheduled sessions which subscribed students may attend.
      </p>
      <p class="text-gray-700 mb-4">
        Students are responsible for joining sessions at the scheduled time using the platform link provided.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">8. Missed Student Sessions</h2>
      <p class="text-gray-700 mb-4">
        If a student fails to attend a scheduled tutoring session, the session will be considered missed. Missed sessions due to student absence are not eligible for refunds, credits, or rescheduling unless agreed at the tutor's discretion.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">9. Tutor Non-Attendance</h2>
      <p class="text-gray-700 mb-4">
        If a tutor fails to attend a scheduled tutoring session (group or one-to-one) without prior notice, the affected student may be eligible for a refund.
      </p>
      <p class="text-gray-700 mb-4">
        To request a refund, the student must notify Bindle by contacting support@bindle.co.uk within a reasonable time following the missed session.
      </p>
      <p class="text-gray-700 mb-4">
        Where tutor non-attendance is confirmed, Bindle will issue a refund equivalent to one week's subscription fee for the affected subscription. The refund may be processed after the next billing cycle and the tutor will not receive payment for the affected session.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">10. Educational Content and Intellectual Property</h2>
      <p class="text-gray-700 mb-4">
        Educational materials provided through the platform, including recorded lessons, documents and resources, are provided for personal educational use only.
      </p>
      <p class="text-gray-700 mb-4">
        Students must not reproduce, distribute, record, sell, upload, or share any tutor content without permission. All intellectual property rights remain with the tutor or Bindle where applicable.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">11. Acceptable Use</h2>
      <p class="text-gray-700 mb-4">
        Students must use the platform respectfully and in accordance with applicable law. Students must not:
      </p>
      <ul class="list-disc pl-6 text-gray-700 mb-4 space-y-2">
        <li>harass tutors or other users,</li>
        <li>share inappropriate or offensive content,</li>
        <li>disrupt lessons,</li>
        <li>attempt to bypass the platform by arranging off-platform tutoring payments, or</li>
        <li>misuse the platform in any way that interferes with its operation.</li>
      </ul>
      <p class="text-gray-700 mb-4">
        Bindle reserves the right to suspend or terminate accounts that breach these rules.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">12. Platform Availability</h2>
      <p class="text-gray-700 mb-4">
        Bindle aims to maintain reliable access to the platform but does not guarantee uninterrupted service. The platform may occasionally be unavailable due to maintenance, technical issues, or third-party service disruptions.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">13. Refund Policy</h2>
      <p class="text-gray-700 mb-4">
        Except as expressly provided in Section 9 (Tutor Non-Attendance), subscription payments are non-refundable. Bindle may issue refunds or adjustments at its discretion where appropriate. Refunds issued through the platform will be processed using the original payment method where possible.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">14. Limitation of Liability</h2>
      <p class="text-gray-700 mb-4">
        To the maximum extent permitted by law, Bindle shall not be liable for:
      </p>
      <ul class="list-disc pl-6 text-gray-700 mb-4 space-y-2">
        <li>academic performance outcomes,</li>
        <li>errors or omissions in tutor-provided content,</li>
        <li>interruptions caused by internet connectivity or third-party software, or</li>
        <li>indirect or consequential losses.</li>
      </ul>
      <p class="text-gray-700 mb-4">
        Nothing in these Terms limits liability where such limitation is not permitted by law.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">15. Governing Law</h2>
      <p class="text-gray-700 mb-4">
        These Terms are governed by the laws of England and Wales. Any disputes arising in connection with these Terms shall be subject to the exclusive jurisdiction of the courts of England and Wales.
      </p>
    </section>
  </div>
</template>
